/**
 * Get help (wave 14a) — the one conversation in this product that is not a
 * knock.
 *
 * Two calls and no third. There is no "read the thread" call, deliberately: the
 * answer arrives by mail at the verified contact address, and a second copy of
 * the correspondence inside the app would be a second place for it to be half
 * right. The list is subjects and states; the screen says where the reply goes.
 *
 * The parsers here follow the same rule as every other module in this
 * directory — TOLERANT OF SHAPE, NEVER OF NAMING. A renamed field fails the
 * seam test rather than rendering as a plausible blank.
 */
import { type RawError, call, get, optTextOf, post, textOf } from "./core";

export type SupportThread = {
  id: string;
  subject: string | null;
  status: string;
  origin: "in_app" | "email" | "other";
  openedAt: string;
  lastMessageAt: string | null;
};

export type SupportThreads = {
  threads: SupportThread[];
  /** Where a reply will land, or null when no address has been proved yet. */
  repliesTo: string | null;
  /** The desk's own inbox — the road that works whatever this port is doing. */
  supportAddress: string;
  /** `simulated`: the whole port is the stand-in. `ready`: the adapter is configured. */
  desk: { simulated: boolean; ready: boolean };
};

function readThread(raw: unknown): SupportThread | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  const id = textOf(r["id"]);
  if (!id) return null;
  const origin = textOf(r["origin"]);
  return {
    id,
    subject: optTextOf(r["subject"]),
    status: textOf(r["status"]) || "open",
    origin: origin === "in_app" || origin === "email" ? origin : "other",
    openedAt: textOf(r["opened_at"]),
    lastMessageAt: optTextOf(r["last_message_at"]),
  };
}

export async function fetchSupportThreads(): Promise<
  ({ ok: true } & SupportThreads) | { ok: false; code: string }
> {
  const answer = await call(() => get("/api/v1/support/conversations"));
  if (!answer) return { ok: false, code: "UNREACHABLE" };
  const { res, body } = answer;
  if (!res.ok) {
    return { ok: false, code: (body as RawError).error?.code ?? "UNKNOWN" };
  }
  const b = (body ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(b["conversations"]) ? b["conversations"] : [];
  const desk = (b["desk"] ?? {}) as Record<string, unknown>;
  return {
    ok: true,
    threads: rows.map(readThread).filter((t): t is SupportThread => t !== null),
    repliesTo: optTextOf(b["replies_to"]),
    supportAddress: textOf(b["support_address"]),
    desk: { simulated: desk["simulated"] === true, ready: desk["ready"] === true },
  };
}

/**
 * Which room the person was standing in when they asked for help. A fourth
 * value named the wallet, and ruling R-0916-1 deleted the room: a context this
 * client can no longer be in is one it must not be able to claim.
 */
export type SupportContextInput = {
  kind: "knock" | "plan" | "door";
  id?: string;
};

export async function openSupportThread(input: {
  subject: string;
  body: string;
  context?: SupportContextInput;
}): Promise<({ ok: true } & { thread: SupportThread }) | { ok: false; code: string }> {
  const answer = await call(() => post("/api/v1/support/conversations", input));
  if (!answer) return { ok: false, code: "UNREACHABLE" };
  const { res, body } = answer;
  if (!res.ok) {
    return { ok: false, code: (body as RawError).error?.code ?? "UNKNOWN" };
  }
  const thread = readThread((body as Record<string, unknown>)["conversation"]);
  return thread === null ? { ok: false, code: "UNKNOWN" } : { ok: true, thread };
}
